import type { CardStyle } from '../models/card-model';
import type { ThemeConfig } from '../models/theme.model';
import { DEFAULT_THEME } from '../defaults/theme.defaults';

export interface ApplyElementThemeVariablesOptions {
	element: HTMLElement;
	themeConfig?: ThemeConfig;
	activeMode: 'light' | 'dark';
	paletteColors: string[];
	cardStyle?: CardStyle;
}

function resolveTypographyWeight(
	weightName: string | undefined,
	typography: ThemeConfig['typography']
): string {
	const trimmed = weightName?.trim();
	if (!trimmed) {
		return String(typography.weight.regular ?? 400);
	}
	const mapped = typography.weight[trimmed as keyof typeof typography.weight];
	if (mapped !== undefined && mapped !== null) {
		return String(mapped);
	}
	return trimmed;
}

function hexToRgbString(color: string): string | null {
	const hex = color.trim().replace(/^#/, '');
	const fullHex =
		hex.length === 3
			? hex
					.split('')
					.map(ch => ch + ch)
					.join('')
			: hex;
	if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) {
		return null;
	}
	const value = Number.parseInt(fullHex, 16);
	const r = (value >> 16) & 255;
	const g = (value >> 8) & 255;
	const b = value & 255;
	return `${r}, ${g}, ${b}`;
}

/** Mirrors {@link JeptoCardWrapperComponent} scoped theme CSS variables on a host element. */
export function applyElementThemeVariables(
	options: ApplyElementThemeVariablesOptions
): void {
	const { element, activeMode, paletteColors, cardStyle } = options;
	const themeConfig = options.themeConfig ?? DEFAULT_THEME;
	const defaultModeColors = DEFAULT_THEME.colors[activeMode];
	const modeColors = themeConfig.colors?.[activeMode] ?? defaultModeColors;
	const colors = {
		...defaultModeColors,
		...modeColors,
		border: {
			...defaultModeColors.border,
			...modeColors.border,
		},
		card: {
			...defaultModeColors.card,
			...modeColors.card,
		},
		text: {
			...defaultModeColors.text,
			...modeColors.text,
		},
		status: {
			...defaultModeColors.status,
			...modeColors.status,
		},
	};
	const card = { ...DEFAULT_THEME.card, ...themeConfig.card };
	const typography = {
		...DEFAULT_THEME.typography,
		...themeConfig.typography,
		size: {
			...DEFAULT_THEME.typography.size,
			...themeConfig.typography?.size,
		},
		lineHeight: {
			...DEFAULT_THEME.typography.lineHeight,
			...themeConfig.typography?.lineHeight,
		},
		weight: {
			...DEFAULT_THEME.typography.weight,
			...themeConfig.typography?.weight,
		},
	};

	element.style.setProperty('--card-border-radius', card.borderRadius);
	element.style.setProperty('--card-border-width', card.borderWidth);
	element.style.setProperty('--card-padding', card.padding);
	element.style.setProperty('--card-spacing', card.spacing);

	element.style.setProperty(
		'--card-background-color',
		colors.card.background
	);
	element.style.setProperty(
		'--card-shadow-color',
		card.shadow === false ? 'transparent' : colors.card.shadow
	);
	element.style.setProperty('--border-primary-color', colors.border.primary);
	element.style.setProperty(
		'--border-secondary-color',
		colors.border.secondary
	);
	element.style.setProperty('--report-background-color', colors.background);
	element.style.setProperty(
		'--primary-background-color',
		colors.card.background
	);
	element.style.setProperty(
		'--background-primary-color',
		colors.card.background
	);
	element.style.setProperty('--background-color', colors.card.background);

	element.style.setProperty('--text-primary-color', colors.text.primary);
	element.style.setProperty('--text-secondary-color', colors.text.secondary);
	element.style.setProperty('--text-link-color', colors.text.link);

	element.style.setProperty('--status-success-color', colors.status.success);
	element.style.setProperty('--status-warning-color', colors.status.warning);
	element.style.setProperty('--status-danger-color', colors.status.danger);
	element.style.setProperty('--status-info-color', colors.status.info);

	element.style.setProperty(
		'--font-family',
		`${typography.fontFamily}, Helvetica, Arial, sans-serif`
	);
	element.style.setProperty(
		'--font-weight-light',
		typography.weight.light.toString()
	);
	element.style.setProperty(
		'--font-weight-normal',
		typography.weight.regular.toString()
	);
	element.style.setProperty(
		'--font-weight-medium',
		typography.weight.medium.toString()
	);
	element.style.setProperty(
		'--font-weight-semibold',
		typography.weight.semibold.toString()
	);
	element.style.setProperty(
		'--font-weight-bold',
		typography.weight.bold.toString()
	);

	const baseSize = parseFloat(typography.baseSize);
	const unit = typography.baseSize.replace(/[\d.]/g, '') || 'px';
	element.style.setProperty(
		'--font-size-xxs',
		`${typography.size.xxs * baseSize}${unit}`
	);
	element.style.setProperty(
		'--font-size-xs',
		`${typography.size.xs * baseSize}${unit}`
	);
	element.style.setProperty(
		'--font-size-sm',
		`${typography.size.sm * baseSize}${unit}`
	);
	element.style.setProperty(
		'--font-size-md',
		`${typography.size.md * baseSize}${unit}`
	);
	element.style.setProperty(
		'--font-size-lg',
		`${typography.size.lg * baseSize}${unit}`
	);
	element.style.setProperty(
		'--font-size-xl',
		`${typography.size.xl * baseSize}${unit}`
	);
	element.style.setProperty(
		'--font-size-xxl',
		`${typography.size.xxl * baseSize}${unit}`
	);

	element.style.setProperty(
		'--line-height-tight',
		`${typography.lineHeight.tight * baseSize}${unit}`
	);
	element.style.setProperty(
		'--line-height-normal',
		`${typography.lineHeight.normal * baseSize}${unit}`
	);
	element.style.setProperty(
		'--line-height-loose',
		`${typography.lineHeight.loose * baseSize}${unit}`
	);
	element.style.setProperty(
		'--line-height-double',
		`${typography.lineHeight.double * baseSize}${unit}`
	);

	element.style.setProperty(
		'--card-title-weight',
		resolveTypographyWeight(card.titleWeight, typography)
	);
	element.style.setProperty(
		'--card-heading-weight',
		resolveTypographyWeight(card.headingWeight, typography)
	);

	paletteColors.slice(0, 10).forEach((color, index) => {
		element.style.setProperty(`--palette-color-${index}`, color);
		const rgb = hexToRgbString(color);
		if (rgb) {
			element.style.setProperty(`--palette-color-${index}-rgb`, rgb);
		} else {
			element.style.removeProperty(`--palette-color-${index}-rgb`);
		}
	});

	const colorOverrides = cardStyle?.colors?.[activeMode]?.card;
	if (colorOverrides) {
		if (colorOverrides.background) {
			element.style.setProperty(
				'--card-background-color',
				colorOverrides.background
			);
			element.style.setProperty(
				'--primary-background-color',
				colorOverrides.background
			);
			element.style.setProperty(
				'--background-primary-color',
				colorOverrides.background
			);
			element.style.setProperty(
				'--background-color',
				colorOverrides.background
			);
		}
		if (colorOverrides.border) {
			element.style.setProperty(
				'--border-primary-color',
				colorOverrides.border
			);
			element.style.setProperty('--card-border-width', '1px');
		}
		if (colorOverrides.shadow) {
			element.style.setProperty(
				'--card-shadow-color',
				colorOverrides.shadow
			);
		}
	}
}
